// Admin › Settings › Referral Program › Referrals — rewards referrals (part 4).
// Every referee under a rewards code: where it stands, how far it is from
// counting (fee, days, shipped orders) and what it paid. Count, reject or take
// back by hand, always with a reason.

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  actOnReferral,
  fetchRewardsReferrals,
  ledgerKey,
  referralsKey,
  type ReferralAction,
  type RewardsReferralRow,
} from '../../../api/referralRewards'
import { Badge } from '../../../components/ui/badge'
import { Button } from '../../../components/ui/button'
import { Card } from '../../../components/ui/card'
import { DataTableShell } from '../../../components/ui/data-table-shell'
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog'
import { Dialog } from '../../../components/ui/dialog.lib'
import { Field } from '../../../components/ui/field'
import { useT } from '../../../i18n'
import {
  rewardsReferralsI18n,
  type RewardsReferralsStrings,
} from '../../../i18n/admin/rewards-referrals'
import { formatCurrency } from '../../../lib/currency'

const TEXTAREA_CLASS =
  'w-full rounded-[var(--field-radius)] border border-[var(--field-border)] bg-[var(--field-bg)] px-[var(--field-padding-x)] py-2 text-[length:var(--field-font-size)] text-[var(--field-text)] placeholder:text-[var(--field-placeholder)] focus-visible:border-[var(--field-focus-border)] focus-visible:outline-none focus-visible:shadow-[var(--field-focus-shadow)]'

const BADGE = {
  signed_up: 'neutral',
  activated: 'info',
  qualified: 'success',
  rejected: 'destructive',
  reversed: 'warning',
} as const

function sellerName(row: RewardsReferralRow): string {
  return row.referee_org_name ?? row.referee_email_masked ?? '—'
}

function ActionDialog({
  row,
  action,
  onClose,
  t,
}: {
  row: RewardsReferralRow
  action: ReferralAction
  onClose: () => void
  t: RewardsReferralsStrings
}) {
  const queryClient = useQueryClient()
  const [reason, setReason] = useState('')
  const [touched, setTouched] = useState(false)
  const ok = reason.trim().length >= 3
  const mutation = useMutation({
    mutationFn: () => actOnReferral(row.referee_user_id, action, reason.trim()),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: referralsKey })
      await queryClient.invalidateQueries({ queryKey: ledgerKey })
      onClose()
    },
  })
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t.dialogTitle[action](sellerName(row))}</DialogTitle>
          <DialogDescription>{t.dialogBody[action]}</DialogDescription>
        </DialogHeader>
        <Field
          id="rewards-referral-reason"
          label={t.reasonLabel}
          required
          error={touched && !ok ? t.reasonRequired : undefined}
        >
          <textarea
            rows={3}
            maxLength={500}
            className={TEXTAREA_CLASS}
            placeholder={t.reasonPlaceholder}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
          />
        </Field>
        {mutation.isError && (
          <p role="alert" className="text-sm text-[var(--color-danger)]">
            {(mutation.error as Error).message}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t.cancel}
          </Button>
          <Button
            variant={action === 'qualify' ? 'primary' : 'destructive'}
            disabled={mutation.isPending}
            onClick={() => {
              setTouched(true)
              if (ok) mutation.mutate()
            }}
          >
            {mutation.isPending ? t.saving : t.actions[action]}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function Progress({ row, t }: { row: RewardsReferralRow; t: RewardsReferralsStrings }) {
  const fee = row.fee_refunded ? t.feeRefunded : row.fee_paid_at ? t.feePaid : t.feeMissing
  return (
    <div className="space-y-0.5 text-xs text-[var(--color-text-muted)]">
      <div className={row.fee_paid_at && !row.fee_refunded ? 'text-[var(--color-text)]' : ''}>{fee}</div>
      <div className="tabular-nums">
        {t.days(row.days_since_fee ?? 0, row.min_days)} · {t.orders(row.shipped_orders, row.min_shipped_orders)}
      </div>
    </div>
  )
}

export function RewardsReferralsCard() {
  const t = useT(rewardsReferralsI18n)
  const query = useQuery({ queryKey: referralsKey, queryFn: fetchRewardsReferrals })
  const [acting, setActing] = useState<{ row: RewardsReferralRow; action: ReferralAction } | null>(null)
  const rows = query.data?.items ?? []

  return (
    <Card className="space-y-3 p-4">
      <div>
        <h2 className="text-base font-semibold text-[var(--color-text)]">{t.title}</h2>
        <p className="mt-0.5 text-sm text-[var(--color-text-muted)]">{t.subtitle}</p>
      </div>
      {query.isLoading ? (
        <p className="text-sm text-[var(--color-text-muted)]">{t.loading}</p>
      ) : query.isError ? (
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span role="alert" className="text-[var(--color-danger)]">
            {t.loadError}
          </span>
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
            {t.retry}
          </Button>
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-[var(--color-text-muted)]">{t.empty}</p>
      ) : (
        <DataTableShell variant="embedded">
          <table>
            <thead>
              <tr>
                <th>{t.columns.seller}</th>
                <th>{t.columns.referrer}</th>
                <th>{t.columns.stage}</th>
                <th>{t.columns.progress}</th>
                <th className="text-right">{t.columns.rewards}</th>
                <th className="text-right">{t.columns.actions}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const open = row.stage === 'activated' || row.stage === 'signed_up'
                return (
                  <tr key={`${row.referral_code_id}:${row.referee_user_id}`}>
                    <td>
                      <div className="font-medium">{sellerName(row)}</div>
                      {row.referee_org_name && row.referee_email_masked && (
                        <div className="text-xs text-[var(--color-text-muted)]">{row.referee_email_masked}</div>
                      )}
                    </td>
                    <td className="text-sm">{row.referrer_email ?? '—'}</td>
                    <td>
                      <Badge variant={BADGE[row.stage]}>{t.stages[row.stage]}</Badge>
                      {row.note && (
                        <div className="mt-0.5 text-xs text-[var(--color-text-muted)]">{row.note}</div>
                      )}
                    </td>
                    <td>
                      <Progress row={row} t={t} />
                    </td>
                    <td className="text-right tabular-nums">
                      {formatCurrency(Number(row.rewards_usd), 'USD')}
                    </td>
                    <td className="text-right">
                      <div className="flex flex-wrap justify-end gap-2">
                        {open && (
                          <>
                            <Button variant="outline" size="sm" onClick={() => setActing({ row, action: 'qualify' })}>
                              {t.actions.qualify}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setActing({ row, action: 'reject' })}>
                              {t.actions.reject}
                            </Button>
                          </>
                        )}
                        {row.stage === 'qualified' && (
                          <Button variant="ghost" size="sm" onClick={() => setActing({ row, action: 'reverse' })}>
                            {t.actions.reverse}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </DataTableShell>
      )}
      {acting && (
        <ActionDialog row={acting.row} action={acting.action} onClose={() => setActing(null)} t={t} />
      )}
    </Card>
  )
}
